import { ModalWindow, UIHeading2 } from '@cian/ui-kit';
import * as React from 'react';
import { useSelector } from 'react-redux';

import {
  InfoPlate,
  InfrastructureTypeRows,
  InfrastructureFromBuilder,
  InsideIcon,
  RateInfo,
} from 'shared/components/TransportAccessibility';
import { MAX_TRANSPORT_ACCESSIBILITY_RATE } from 'shared/constants/transportAccessibility';
import { getTransportAccessibilityInfrastructure } from 'shared/selectors/newbuildingMap';
import {
  selectInfrastructureFromBuilder,
  selectTransportAccessibilitySource,
} from 'shared/selectors/transportAccessibility';
import { TTAInfrastructureType } from 'shared/types/map';
import { IInfrastructureFromBuilder } from 'shared/types/transportAccessibility';

import { getInsideRows } from './helpers';
import { prepareRate } from '../../../TransportAccessibilityRate/helpers';

export interface ITransportAccessibilityOutsideProps {
  onTransportAccessibilityChange: (infrastructureType: TTAInfrastructureType | null) => void;
}

export const TransportAccessibilityInside: React.FC<ITransportAccessibilityOutsideProps> = ({
  onTransportAccessibilityChange,
}) => {
  const { rate, title, rateDetails } = useSelector(selectInfrastructureFromBuilder) as IInfrastructureFromBuilder;
  const departmentLink = useSelector(selectTransportAccessibilitySource);

  const displayRate = prepareRate(rate);
  const displayMaxRate = String(MAX_TRANSPORT_ACCESSIBILITY_RATE);

  const transportAccesibilityInfrastructure = useSelector(getTransportAccessibilityInfrastructure);
  const { clickableRows, nonClickableRows } = React.useMemo(
    () => getInsideRows(transportAccesibilityInfrastructure),
    [transportAccesibilityInfrastructure],
  );

  const [isModalOpen, setModal] = React.useState(false);
  const openModal = React.useCallback(() => setModal(true), []);
  const closeModal = React.useCallback(() => setModal(false), []);

  const [selectedType, setSelectedType] = React.useState(clickableRows[0]?.id);

  React.useEffect(() => {
    onTransportAccessibilityChange(selectedType as TTAInfrastructureType);

    return () => onTransportAccessibilityChange(null);
  }, [onTransportAccessibilityChange]);

  const handleRowClick = React.useCallback((infrastructureType: TTAInfrastructureType) => {
    onTransportAccessibilityChange(infrastructureType);
    setSelectedType(infrastructureType);
  }, []);

  return (
    <>
      <InfoPlate
        icon={<InsideIcon />}
        content={<RateInfo onInfoIconClick={openModal} rate={rate} rateText={prepareRate(rate)} />}
        data-testid="TransportAccessibilityInsideInfo"
        withHorizontalMargin
      />
      <InfrastructureTypeRows
        clickableRows={clickableRows}
        nonClickableRows={nonClickableRows}
        onRowClick={handleRowClick}
        selectedItemId={selectedType}
      />
      <ModalWindow
        content={
          <InfrastructureFromBuilder
            departmentLink={departmentLink}
            maxRate={displayMaxRate}
            rate={displayRate}
            terms={rateDetails}
          />
        }
        header={<UIHeading2>{title}</UIHeading2>}
        escape
        width={759}
        onClose={closeModal}
        open={isModalOpen}
        size="M"
      />
    </>
  );
};
